import React, { useState, useEffect, RefObject } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

interface UseHeaderNavigationProps {
  headerRef: RefObject<HTMLElement>;
  searchRef: RefObject<HTMLElement>;
  setShowSuggestions: (show: boolean) => void;
}

/**
 * Hook to manage header navigation states (menus, overlays, dropdowns).
 */
export function useHeaderNavigation({ headerRef, searchRef, setShowSuggestions }: UseHeaderNavigationProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileSearchFocused, setIsMobileSearchFocused] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Header and mobile menu outside clicks
      if (headerRef.current && !headerRef.current.contains(event.target as Node)) {
        setActiveDropdown(null);
        setIsMobileMenuOpen(false);
      }
      
      // Desktop search suggestions outside clicks
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowSuggestions(false);
        setActiveDropdown(null);
        setIsMobileSearchFocused(false);
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [headerRef, searchRef, setShowSuggestions]);

  useEffect(() => {
    if (isMobileSearchFocused || isMobileMenuOpen) {
      const originalOverflow = document.body.style.overflow;
      const originalOverscroll = document.body.style.overscrollBehavior;
      const originalTouchAction = document.body.style.touchAction;

      document.body.style.overflow = 'hidden';
      document.body.style.overscrollBehavior = 'contain';
      document.body.style.touchAction = 'none';

      return () => {
        document.body.style.overflow = originalOverflow;
        document.body.style.overscrollBehavior = originalOverscroll;
        document.body.style.touchAction = originalTouchAction;
      };
    }
  }, [isMobileSearchFocused, isMobileMenuOpen]);

  const scrollToSection = (
    e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
    href: string
  ) => {
    const isHash = href.startsWith('#');
    
    if (isHash) {
      e.preventDefault();
      
      // Normalize href to ensure it's a valid link starting from root
      const normalizedHref = href === '#' ? '/' : (href.startsWith('/') ? href : `/${href}`);
      
      // If we're not on the home page (or the base path of the hash), go there
      if (location.pathname !== '/') {
        navigate(normalizedHref);
      } else {
        const targetId = href.substring(1);
        const element = targetId ? document.getElementById(targetId) : null;

        if (href === '#') {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else if (element) {
          const headerOffset = 100;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }
    } else if (href.startsWith('/')) {
      e.preventDefault();
      navigate(href);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  };

  return {
    isMobileMenuOpen,
    setIsMobileMenuOpen,
    isMobileSearchFocused,
    setIsMobileSearchFocused,
    activeDropdown,
    setActiveDropdown,
    scrollToSection
  };
}
